import React, { useState, useMemo, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, Button, IconButton, CheckboxV2, Icon } from '@shopflo/ui'
import { X, Cube } from '@phosphor-icons/react'
import { useLocale } from 'src/lib/hooks/useLocal'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { CatalogueDataType } from 'src/lib/types/common/catalogue'
import { getProductById } from 'src/lib/core/api-methods'
import useSWR from 'swr'

interface VariantEditorDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  product: CatalogueDataType | null
  onApply: (updatedProduct: CatalogueDataType | null) => void
  singleSelect?: boolean
}

const VariantEditorDialog: React.FC<VariantEditorDialogProps> = ({
  open,
  onOpenChange,
  product,
  onApply,
  singleSelect = false
}) => {
  const { t } = useLocale()
  const {
    getters: { merchant }
  } = useMerchant()
  const accountId = merchant?.accountId
  const issuerId = process.env.NEXT_PUBLIC_APP_ISSUER_ID ?? ''

  const [selectedVariantIds, setSelectedVariantIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (open && product?.variants) {
      const initialIds = new Set(product.variants.map(variant => variant.id || ''))
      setSelectedVariantIds(initialIds)
    }
  }, [open, product])

  const { data: fetchedProductData, isLoading } = useSWR(
    () => {
      if (open && product?.id) return ['product-variants', product.id]
      return null
    },
    async () => {
      const response: any = await getProductById(issuerId, accountId, product?.id || null)
      if (response?.data?.data) {
        return response.data.data
      }
      return null
    }
  )

  const allVariants: CatalogueDataType[] = useMemo(() => {
    if (!fetchedProductData?.variants) return []
    return fetchedProductData.variants.map((variant: any) => ({
      id: variant?.variant_id,
      description: variant?.title || variant?.name || 'Variant',
      price: variant?.current_price ?? variant?.original_price ?? 0,
      inventory: variant?.inventory_quantity || variant?.inventory || 0,
      isVariant: true
    }))
  }, [fetchedProductData])

  const filteredVariants = useMemo(() => {
    return allVariants
  }, [allVariants])

  const handleToggleVariant = (variantId: string) => {
    setSelectedVariantIds(prev => {
      if (singleSelect) {
        return prev.has(variantId) ? new Set() : new Set([variantId])
      }
      const next = new Set(prev)
      if (next.has(variantId)) {
        next.delete(variantId)
      } else {
        next.add(variantId)
      }
      return next
    })
  }

  const handleApply = () => {
    if (!product) return

    const selectedVariantsList = allVariants.filter(v => selectedVariantIds.has(v.id as string))

    if (selectedVariantsList.length === 0) {
      onApply(null)
      onOpenChange(false)
      return
    }

    const updatedProduct: CatalogueDataType = {
      ...product,
      variants: selectedVariantsList,
      totalVariantCount: allVariants.length || product.totalVariantCount // Update total count if we have better info
    }

    onApply(updatedProduct)
    onOpenChange(false)
  }

  const renderContent = () => {
    if (isLoading) {
      return <div className='flex justify-center items-center h-full text-zinc-500 text-sm'>{t('loading')}</div>
    }

    if (filteredVariants.length === 0) {
      return (
        <div className='flex justify-center items-center h-full text-zinc-500 text-sm'>
          {t('product_search_dialog.no_variants_found')}
        </div>
      )
    }

    return (
      <div className='flex flex-col'>
        {filteredVariants.map(variant => (
          <div key={variant.id} className='grid grid-cols-[3fr_1fr_1fr] gap-4 py-3 border-zinc-100 items-center'>
            <div className='flex items-center gap-3'>
              <CheckboxV2
                size='sm'
                checked={selectedVariantIds.has(variant.id as string)}
                onCheckedChange={() => handleToggleVariant(variant.id as string)}
              />
              <span className='text-text-sm-medium text-zinc-800 truncate' title={variant.description}>
                {variant.description}
              </span>
            </div>
            <div className='text-right text-text-sm-regular text-zinc-600'>{variant.inventory}</div>
            <div className='text-right text-text-sm-regular text-zinc-600 pr-1'>₹{variant.price}</div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='min-w-[600px] bg-white text-black z-[50] !rounded-2xl p-0'>
        <DialogHeader>
          <div className='px-5 pt-4 flex items-center'>
            <div className='flex gap-3 items-center'>
              <div className='h-10 w-10 rounded-xl flex items-center justify-center bg-zinc-100'>
                <Icon IconComponent={Cube} size='lg' className='text-zinc-500' iconProps={{ weight: 'duotone' }} />
              </div>
              <div>
                <h3 className='text-text-lg-semibold text-zinc-900'>{t('product_search_dialog.edit_variants')}</h3>
                <p className='text-text-xs-regular text-zinc-500'>{product?.description}</p>
              </div>
            </div>

            <IconButton
              IconComponent={X}
              size='sm'
              onClick={() => onOpenChange(false)}
              className='ml-auto'
              variant='ghost'
            />
          </div>
        </DialogHeader>

        <div className='flex flex-col h-[500px]'>
          <div className='grid grid-cols-[3fr_1fr_1fr] gap-4 px-5 py-3 border-b-1 border-zinc-100 '>
            <div className='flex items-center gap-3'>
              <span className='text-text-xs-medium text-zinc-500'>{t('product_search_dialog.variant_name')}</span>
            </div>
            <div className='text-right text-text-xs-medium text-zinc-500'>{t('product_search_dialog.quantity')}</div>
            <div className='text-right text-text-xs-medium text-zinc-500 pr-1'>{t('price')}</div>
          </div>

          <div className='flex-1 overflow-y-auto px-5'>{renderContent()}</div>

          <div className='p-5 border-t border-zinc-100 flex justify-end gap-3'>
            <Button variant='outline' size='md' onClick={() => onOpenChange(false)}>
              {t('cancel')}
            </Button>
            <Button size='md' onClick={handleApply}>
              {t('done')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default VariantEditorDialog
